// posMobileShared.tsx — primitives partagés par la vue POS mobile
// À déposer dans : src/gestion-pos/posMobileShared.tsx
import React, { useState } from "react";

/* ───────── Format CFA (fr-FR) ───────── */
export const fmt = (v: number) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Math.round(Number(v) || 0));

/* ───────── Jeu d'icônes en ligne (style Ant Outlined / Lucide) ───────── */
const P: Record<string, React.ReactNode> = {
  search: <><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  chevron: <path d="M9 6l6 6-6 6" />,
  chevronUp: <path d="M6 15l6-6 6 6" />,
  back: <path d="M15 6l-6 6 6 6" />,
  x: <path d="M6 6l12 12M18 6L6 18" />,
  bell: <><path d="M6 9a6 6 0 1112 0c0 5 2 6 2 6H4s2-1 2-6z" /><path d="M10 20a2 2 0 004 0" /></>,
  list: <><path d="M8 6h13M8 12h13M8 18h13" /><circle cx="3.5" cy="6" r="1.2" /><circle cx="3.5" cy="12" r="1.2" /><circle cx="3.5" cy="18" r="1.2" /></>,
  cart: <><circle cx="9" cy="20" r="1.5" /><circle cx="18" cy="20" r="1.5" /><path d="M2 3h3l2.4 12.2a1.5 1.5 0 001.5 1.2h8.6a1.5 1.5 0 001.5-1.2L21 7H6" /></>,
  trash: <path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M6 7l1 13h10l1-13" />,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.5-7 8-7s8 3 8 7" /></>,
  scan: <><path d="M4 7V5a1 1 0 011-1h2M17 4h2a1 1 0 011 1v2M20 17v2a1 1 0 01-1 1h-2M7 20H5a1 1 0 01-1-1v-2" /><path d="M4 12h16" /></>,
  store: <><path d="M4 9l1.4-4.2A1 1 0 016.3 4h11.4a1 1 0 01.9.8L20 9" /><path d="M4 9a2.5 2.5 0 005 0 2.5 2.5 0 005 0 2.5 2.5 0 005 0M5 11v9h14v-9" /></>,
  banknote: <><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /></>,
  phone: <><rect x="6" y="2" width="12" height="20" rx="2.5" /><path d="M11 18h2" /></>,
  card: <><rect x="2" y="5" width="20" height="14" rx="2.5" /><path d="M2 10h20" /></>,
  wallet: <><path d="M3 7a2 2 0 012-2h12a2 2 0 012 2H5a2 2 0 00-2 2" /><path d="M3 9v8a2 2 0 002 2h13a2 2 0 002-2v-6a2 2 0 00-2-2H5" /><circle cx="17" cy="14" r="1.3" fill="currentColor" stroke="none" /></>,
  printer: <><path d="M6 9V3h12v6" /><rect x="4" y="9" width="16" height="8" rx="2" /><path d="M7 17h10v4H7z" /></>,
  receipt: <><path d="M5 3v18l2-1.3 2 1.3 2-1.3 2 1.3 2-1.3 2 1.3V3l-2 1.3L15 3l-2 1.3L11 3 9 4.3 7 3 5 3z" /><path d="M8.5 8.5h7M8.5 12h7M8.5 15.5h4" /></>,
  file: <><path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8l-5-5z" /><path d="M14 3v5h5" /></>,
  percent: <><path d="M19 5L5 19" /><circle cx="7.5" cy="7.5" r="2.5" /><circle cx="16.5" cy="16.5" r="2.5" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><circle cx="12" cy="8" r="0.6" fill="currentColor" /></>,
  alert: <><path d="M12 3l9.5 16.5H2.5L12 3z" /><path d="M12 10v4" /><circle cx="12" cy="17" r="0.6" fill="currentColor" /></>,
  check: <path d="M5 12l5 5L20 7" />,
  checkCircle: <><circle cx="12" cy="12" r="9" /><path d="M8.5 12.2l2.4 2.4 4.6-5" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>,
  arrowRight: <path d="M5 12h14M13 6l6 6-6 6" />,
  box: <><path d="M3 8l9-5 9 5v8l-9 5-9-5V8z" /><path d="M3 8l9 5 9-5M12 13v8" /></>,
};
export function Icon({ name, size = 20, stroke = 2, style }: { name: string; size?: number; stroke?: number; style?: React.CSSProperties; }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={style}>
      {P[name] || null}
    </svg>
  );
}

/* accent par produit (déterministe d'après l'id) */
export const ACCENTS = [
  { bg: "var(--rf-primary-soft)", fg: "var(--rf-primary)" },
  { bg: "var(--rf-green-bg)", fg: "var(--rf-green-d)" },
  { bg: "var(--rf-amber-bg)", fg: "var(--rf-amber)" },
  { bg: "var(--rf-violet-bg)", fg: "var(--rf-violet)" },
  { bg: "var(--rf-red-bg)", fg: "var(--rf-red-d)" },
  { bg: "var(--rf-slate-bg)", fg: "var(--rf-slate)" },
];
export const accOf = (id: number) => ACCENTS[Math.abs(id) % ACCENTS.length];

/* mode espèces ? (affiche le pavé + le rendu) */
export const isCashMode = (m?: { name: string } | null) =>
  !!m && /esp[eè]ce|cash|liquide|comptant/i.test(m.name);
export const iconForMode = (name: string) =>
  /esp[eè]ce|cash|liquide|comptant/i.test(name) ? "banknote"
    : /mobile|momo|orange|moov|wave|airtel/i.test(name) ? "phone"
      : /carte|card|visa|master|bancaire/i.test(name) ? "card"
        : "wallet";

/* statuts (libellé + pastille) */
export const ORDER_STATUS_META: Record<string, { label: string; cls: string }> = {
  new:     { label: "Nouvelle",   cls: "rf-pill-blue"  },
  kitchen: { label: "En cuisine", cls: "rf-pill-amber" },
  ready:   { label: "Prête",      cls: "rf-pill-green" },
};
export const TICKET_STATUS_META: Record<string, { label: string; cls: string }> = {
  paid:    { label: "Payé",     cls: "rf-pill-green" },
  partial: { label: "Partiel",  cls: "rf-pill-amber" },
  unpaid:  { label: "Non payé", cls: "rf-pill-red"   },
};

/* ───────── Types (sous-ensembles des types réels de GestionPos.tsx) ───────── */
export interface Product {
  id_produit: number;
  item_type?: "product" | "service";
  product_id?: number | null;
  service_id?: number | null;
  nom_produit: string;
  prix_vente: number;
  quantite_stock: number | null;
  image: string;
  id_categorie: number;
  code_qr?: string;
  stock_management_mode?: "standard" | "pre_order" | "unlimited";
  duration_minutes?: number | null;
}
export interface CartItem extends Omit<Product, "quantite_stock"> { cartQuantity: number; }
export interface Category { id: number; categorie: string; }
export interface Customer { ID_Client: number; Nom_Raison_Sociale: string; }
export interface PaymentMode { id: number; name: string; }
export interface PosOrder {
  ref: string; place: string; source: string; items: number; total: number;
  status: "new" | "kitchen" | "ready"; mins: number;
}
export interface PosTicket {
  ref: string; time: string; client: string; mode: string;
  total: number; paid: number; status: "paid" | "partial" | "unpaid";
}
export interface PosSaleResult {
  ref: string; count: number; client: string; methodLabel: string;
  isCash: boolean; received: number; change: number; total: number;
}

/* ───────── Vignette produit (image réelle + repli initiale) ───────── */
export function Thumb({ product, url, size }: { product: Product | CartItem; url: string | null; size: number; }) {
  const [broken, setBroken] = useState(false);
  const acc = accOf(product.id_produit);
  if (url && !broken) {
    return <img src={url} alt="" onError={() => setBroken(true)}
      style={{ width: "100%", height: "100%", objectFit: "cover" }} />;
  }
  return (
    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: acc.bg }}>
      <Icon name="box" size={size} stroke={1.6} style={{ color: acc.fg }} />
    </div>
  );
}
